import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Search, FileText, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';

const API = '/api';

// THE ARCHIVE — its own screen, deliberately not a tab on Approvals.
//
// Approvals is a QUEUE: you work it and empty it, and an empty one means you are done. This
// is a RECORD, kept forever, and it only grows. Sharing a screen would make the archive
// inherit the queue's shape and the queue inherit the archive's growth.
//
// Until this existed, a signed agreement was reachable only through a link in an email or by
// knowing the reference. For the person who countersigns them, that was the wrong way round.

const STATUS_META = {
  pending_review: { label: 'Pending review', cls: 'text-yellow-300 border-yellow-400/40 bg-yellow-400/10' },
  approved: { label: 'Approved', cls: 'text-hg-green border-hg-green/40 bg-hg-green/10' },
  declined: { label: 'Declined', cls: 'text-red-400 border-red-500/40 bg-red-500/10' },
  invited: { label: 'Invited', cls: 'text-hg-text-dim border-hg-border bg-hg-surface' },
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return String(s).slice(0, 10); }
};

function Chip({ tone, title, children }) {
  const cls = tone === 'green'
    ? 'text-hg-green border-hg-green/40 bg-hg-green/10'
    : tone === 'amber'
      ? 'text-yellow-300 border-yellow-400/40 bg-yellow-400/10'
      : 'text-hg-text-dim border-hg-border bg-hg-surface';
  return (
    <span title={title}
      className={`inline-flex items-center gap-1 text-[10px] font-sans rounded-sm px-1.5 py-0.5 border whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
}

export default function AgreementArchive() {
  const [rows, setRows] = useState([]);
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState('');
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openingId, setOpeningId] = useState('');
  const debounce = useRef(null);

  const load = useCallback(async (q, st, sup) => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/agreements`, {
        // SERVER-SIDE search. This collection only grows, and filtering in the browser
        // means shipping every agreement to it in order to find one.
        params: { q: q || undefined, status: st || undefined,
                  include_superseded: sup || undefined, limit: 500 },
      });
      setRows(r.data?.agreements || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load agreements.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load('', '', false); }, [load]);

  const onSearch = (v) => {
    setTerm(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(v, status, includeSuperseded), 250);
  };

  // The PDF comes through the auth-gated route, which signs a fresh Cloudinary URL per
  // request. Never a stored URL: an authenticated URL carries a signature that does not
  // expire, so keeping one would put a durable credential in the page.
  const openPdf = async (id) => {
    setOpeningId(id);
    setError('');
    try {
      const r = await axios.get(`${API}/agreements/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError('Could not open that PDF.');
    }
    setOpeningId('');
  };

  return (
    <div className="space-y-4" data-testid="agreement-archive">
      <div>
        <h2 className="text-lg font-sans text-hg-text">Agreements</h2>
        <p className="text-xs font-sans text-hg-text-dim mt-1">
          Every agreement, not just the ones waiting on you. Approvals is the queue; this is
          the record.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-hg-text-dim" />
          <input
            data-testid="aa-search"
            value={term}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search name, business, email or reference"
            className="w-full bg-white border border-hg-border rounded-sm pl-8 pr-3 py-2.5 sm:py-1.5 min-h-[44px] sm:min-h-0 text-base sm:text-xs font-sans text-hg-text"
          />
        </div>
        <select
          data-testid="aa-status"
          value={status}
          onChange={(e) => { setStatus(e.target.value); load(term, e.target.value, includeSuperseded); }}
          className="bg-white border border-hg-border rounded-sm px-3 py-2.5 sm:py-1.5 min-h-[44px] sm:min-h-0 text-base sm:text-xs font-sans text-hg-text"
        >
          <option value="">All statuses</option>
          <option value="pending_review">Pending review</option>
          <option value="approved">Approved</option>
          <option value="declined">Declined</option>
          <option value="invited">Invited</option>
        </select>
        <label className="flex items-center gap-1.5 text-[11px] font-sans text-hg-text-dim">
          <input
            type="checkbox"
            data-testid="aa-superseded"
            checked={includeSuperseded}
            onChange={(e) => { setIncludeSuperseded(e.target.checked); load(term, status, e.target.checked); }}
          />
          Include superseded
        </label>
        <button
          onClick={() => load(term, status, includeSuperseded)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text-dim"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error && <div className="text-xs text-red-400 font-sans" data-testid="aa-error">{error}</div>}

      {loading ? (
        <div className="flex items-center gap-2 text-xs font-sans text-hg-text-dim">
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs font-sans text-hg-text-dim" data-testid="aa-empty">
          {term ? `Nothing matches “${term}”.` : 'No agreements yet.'}
        </div>
      ) : (
        <div className="space-y-2" data-testid="aa-rows">
          {rows.map((a) => (
            <div key={a.agreement_id}
              data-testid={`aa-row-${a.reference}`}
              className="bg-hg-surface border border-hg-border rounded-sm p-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-sans text-hg-text truncate">
                  {a.contact_name || a.business_name || '(no name)'}
                  {a.business_name && a.contact_name && (
                    <span className="text-hg-text-dim"> · {a.business_name}</span>
                  )}
                </div>
                <div className="text-[11px] font-sans text-hg-text-dim truncate">
                  {a.email} · ref {a.reference} · signed {fmtDate(a.signed_at || a.created_at)}
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${(STATUS_META[a.status] || STATUS_META.invited).cls}`}>
                  {(STATUS_META[a.status] || {}).label || a.status}
                </span>

                {/* Executed vs licensee-only, at a glance. That distinction now exists and
                    it matters: one is a contract both parties signed, the other is a copy
                    carrying one signature. */}
                {a.executed ? (
                  <Chip tone="green" title={`Countersigned ${fmtDate(a.countersigned_at)} by ${a.countersigned_by || '—'}`}>
                    <CheckCircle2 size={10} /> Executed
                  </Chip>
                ) : a.status !== 'invited' ? (
                  <Chip tone="amber" title="Carries the licensee's signature only — Henry-Griffitts has not countersigned.">
                    <AlertTriangle size={10} /> Licensee only
                  </Chip>
                ) : null}

                {/* Which wording they agreed to. Once the terms change after legal review,
                    this stops being trivia. */}
                {a.terms_version && (
                  <Chip title="The wording this signer accepted">terms {a.terms_version}</Chip>
                )}
                {a.is_test && <Chip tone="amber">TEST</Chip>}
                {a.superseded_by && <Chip title="Replaced by a later agreement">superseded</Chip>}
              </div>

              <button
                data-testid={`aa-pdf-${a.reference}`}
                onClick={() => openPdf(a.agreement_id)}
                disabled={!a.has_pdf || openingId === a.agreement_id}
                title={a.has_pdf ? 'Open the signed PDF' : 'No signed PDF on this agreement'}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border rounded-sm text-hg-text disabled:opacity-40"
              >
                {openingId === a.agreement_id
                  ? <Loader2 size={12} className="animate-spin" />
                  : <FileText size={12} />}
                PDF
              </button>
            </div>
          ))}
          <div className="text-[11px] font-sans text-hg-text-dim pt-1">
            {rows.length} agreement{rows.length === 1 ? '' : 's'}
            {!includeSuperseded && ' · superseded hidden'}
          </div>
        </div>
      )}
    </div>
  );
}
